import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { getLocalStorage } from '../../lib/get-local-storage'

import styles from './styles/SubscriptionBar.css'
import { WarningIcon, XIcon } from 'components/icons/Icon'
import { PLANS, SUBSCRIPTION_STATUS } from '../../../server/lib/constants'
import * as analytics from './../../lib/analytics'
import { reenableSubscription } from '../../store/ui/actions'

const STORAGE_KEY = 'subBannerDismissed'

const storageKeyFor = user => {
  const suffix = user?.email ? `-${user.email.trim().toLowerCase()}` : ''
  return `${STORAGE_KEY}${suffix}`
}

const SubscriptionBannerLoaded = ({ user, storageKey, setShow }) => {
  const dispatch = useDispatch()

  const dismiss = useCallback(() => {
    setShow(false)
    getLocalStorage()?.setItem(storageKey, 'true')

    analytics.event({
      category: 'Auto-renew banner',
      action: 'Dismiss',
      label: 'Subscription'
    })
  }, [setShow, storageKey])

  const style = useMemo(() => {
    const date = user?.subscription?.renewDate
    if (!date) return styles.alert

    const expiry = new Date(date).getTime()
    const diffDays = (expiry - Date.now()) / (1000 * 60 * 60 * 24)

    if (diffDays <= 1) return styles.danger
    if (diffDays <= 15) return styles.warning

    return styles.alert
  }, [user?.subscription?.renewDate])

  const dateInfo = useMemo(() => {
    const date = user?.subscription?.renewDate
    if (!date) return ''
    return `on ${new Date(date).toLocaleDateString()}`
  }, [user?.subscription?.renewDate])

  const handleTurnBackOn = async e => {
    e.preventDefault()
    dispatch(reenableSubscription())
  }

  return (
    <div className={classNames(styles.subscriptionBar, style)}>
      <WarningIcon className={styles.icon} aria-label='Attention' />
      <div className={styles.text}>
        <p>
          You've canceled your auto-renew. Your subscription will end {dateInfo || 'soon'} and you'll lose access to
          your premium features.
        </p>
        <button
          type='button'
          className={styles.buttonLink}
          onClick={handleTurnBackOn}
          aria-label='Turn auto-renew back on'
        >
          Turn auto-renew back on.
        </button>
      </div>
      <button type='button' className={styles.closeButton} onClick={dismiss} aria-label='Dismiss subscription notice'>
        <XIcon className={styles.closeIcon} aria-hidden='true' />
      </button>
    </div>
  )
}

const SubscriptionBanner = () => {
  const user = useSelector(({ ui }) => ui.user)
  const [show, setShow] = useState(false)

  const storageKey = storageKeyFor(user)

  useEffect(() => {
    if (!user?.subscription || user?.organizationLicense) {
      setShow(false)
      return
    }

    const sub = user.subscription

    // don't show if the subscription/trial has already ended
    const renewDate = sub?.renewDate ? new Date(sub.renewDate) : null
    const hasExpired = renewDate && renewDate.getTime() < Date.now()

    // Clear the dismiss flag when the subscription is active again
    // so the banner can re-appear if the user cancels a second time.
    const isActiveOrTrialing = [SUBSCRIPTION_STATUS.trialing, SUBSCRIPTION_STATUS.active].includes(sub?.status)
    if (isActiveOrTrialing) {
      getLocalStorage()?.removeItem(storageKey)
      setShow(false)
      return
    }

    // Primary check: cancelAtPeriodEnd from Stripe (via backend). The fallback
    // covers rows written before cancelAtPeriodEnd was stored, so it applies
    // only while the field is unknown — an explicit `false` means Stripe has
    // told us there is no pending cancel, and offering "turn auto-renew back
    // on" for that state can only no-op (SCI-3461).
    const isCanceled =
      sub?.cancelAtPeriodEnd ||
      (sub?.cancelAtPeriodEnd !== false && sub?.plan === PLANS.premium && sub?.status === SUBSCRIPTION_STATUS.inactive)

    if (!isCanceled || hasExpired) {
      setShow(false)
      return
    }

    if (getLocalStorage()?.getItem(storageKey) === 'true') {
      setShow(false)
      return
    }

    analytics.event({
      category: 'Auto-renew banner',
      action: 'Show'
    })

    setShow(true)
  }, [user?.subscription, storageKey])

  if (!show) return null
  return <SubscriptionBannerLoaded user={user} storageKey={storageKey} setShow={setShow} />
}

export default SubscriptionBanner
